// RootLayout — chrome around every authenticated route: sidebar, role switcher,
// settings modal and the patient slide-over.
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { ThemeToggle } from '@platform/witems';
import PhoneCallBanner from './PhoneCallBanner';
import Media from './Media';
import { AppErrorBoundary } from './AppErrorBoundary';
import { useAppData } from '../hooks/useAppData';
import useIdleAware from '../hooks/useIdleAware';
import {
  ROLE_CONFIG, ROLE_TABS, getRoleConfig, normalizeRoleId,
} from '../data/roles';
import { getSessionKey, setSessionKey } from '../lib/session';
import { useCurrentUser } from '../state/data/account';
import { useUnreadConversationCount } from '../state/data/dashboard';
import { logout } from '../lib/account';
import { getLastSelectedProgramId } from '../lib/selectedProgram';

function computeInitials(
  firstname?: string,
  lastname?: string,
  email?: string,
): string {
  const f = (firstname ?? '').trim();
  const l = (lastname ?? '').trim();
  if (f && l) return (f[0]! + l[0]!).toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  if (l) return l.slice(0, 2).toUpperCase();
  const e = (email ?? '').trim();
  if (e) return e.slice(0, 2).toUpperCase();
  return '?';
}

const ICON = {
  home: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  engage: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  switchTeam: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

const ALL_NAV_ITEMS: Array<{ id: 'home' | 'engage'; label: string; icon: React.ReactElement }> = [
  { id: 'home', label: 'Dashboard', icon: ICON.home },
  { id: 'engage', label: 'Engage', icon: ICON.engage },
];

const UNREAD_REFRESH_MS = 60_000;
const IDLE_THRESHOLD_MS = 120_000;

// Match anything under /team/:programid (with or without /patient/:id segment).
const TEAM_PATH_RE = /^\/team\/[^/]+(\/.*)?$/;

function isHomePath(pathname: string): boolean {
  // The "home" sidebar entry highlights for the picker page (`/`), the
  // patient list (`/patients`), and any /team/:programid[/...] route.
  return pathname === '/' || pathname.startsWith('/patients') || TEAM_PATH_RE.test(pathname);
}

function isActive(pathname: string, navId: 'home' | 'engage'): boolean {
  if (navId === 'home') return isHomePath(pathname);
  if (navId === 'engage') return pathname === '/engage' || pathname.startsWith('/engage/');
  return false;
}

// Map a URL path to its sidebar tab id.
function pathToTab(path: string): 'home' | 'engage' | null {
  if (isHomePath(path)) return 'home';
  if (path.startsWith('/engage')) return 'engage';
  return null;
}

export default function RootLayout() {
  const {
    userRole, handleRoleChange, loading, error, loadData,
  } = useAppData();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: currentUserData } = useCurrentUser();
  const account = currentUserData?.account ?? null;
  const currentUser = currentUserData?.currentUser ?? null;

  // The base handleRoleChange in App.tsx only updates state + localStorage;
  // here we wrap it to redirect to home when the new role can't see the
  // current tab. window.history.replaceState bypassed react-router and
  // silently kept the previous route mounted.
  const handleRoleChangeRouted = useCallback((role: string) => {
    handleRoleChange(role);
    const normalized = normalizeRoleId(role);
    const allowedTabs = ROLE_TABS[normalized as keyof typeof ROLE_TABS] || ROLE_TABS.clinician;
    const path = location.pathname;
    const tabFromPath = pathToTab(path);
    if (tabFromPath && !allowedTabs.includes(tabFromPath)) {
      navigate('/', { replace: true });
    }
  }, [handleRoleChange, location.pathname, navigate]);

  // Settings modal state stays local to the layout.
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [sessionInput, setSessionInput] = useState('');

  // Profile + role dropdowns. Both rendered via portal so they can escape
  // the sidebar's `overflow: hidden` (which would otherwise clip a menu
  // that sits to the right of the 64px-wide sidebar).
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ left: number; bottom: number } | null>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [roleAnchor, setRoleAnchor] = useState<{ left: number; bottom: number } | null>(null);
  const roleButtonRef = useRef<HTMLButtonElement>(null);
  const roleMenuRef = useRef<HTMLDivElement>(null);

  const closeProfileMenu = useCallback(() => setProfileMenuOpen(false), []);
  const closeRoleMenu = useCallback(() => setRoleMenuOpen(false), []);

  function toggleProfileMenu() {
    if (profileMenuOpen) {
      closeProfileMenu();
      return;
    }
    const rect = profileButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuAnchor({
        left: rect.right + 8,
        bottom: window.innerHeight - rect.bottom,
      });
    }
    setProfileMenuOpen(true);
  }

  function toggleRoleMenu() {
    if (roleMenuOpen) {
      closeRoleMenu();
      return;
    }
    const rect = roleButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setRoleAnchor({
        left: rect.right + 8,
        bottom: window.innerHeight - rect.bottom,
      });
    }
    setRoleMenuOpen(true);
  }

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProfileMenu();
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (profileButtonRef.current?.contains(target)) return;
      if (profileMenuRef.current?.contains(target)) return;
      closeProfileMenu();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [profileMenuOpen, closeProfileMenu]);

  useEffect(() => {
    if (!roleMenuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRoleMenu();
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (roleButtonRef.current?.contains(target)) return;
      if (roleMenuRef.current?.contains(target)) return;
      closeRoleMenu();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [roleMenuOpen, closeRoleMenu]);

  function openSettings() {
    closeProfileMenu();
    setSessionInput(getSessionKey() || '');
    setShowSettingsModal(true);
  }

  function saveSettings() {
    const trimmed = sessionInput.trim();
    if (trimmed) setSessionKey(trimmed);
    setShowSettingsModal(false);
    // Trigger a refetch instead of a full page reload so call/chat state,
    // role gate, and any modal-driven UI survive the session-key swap.
    if (trimmed) loadData();
  }

  // Engage badge — its own query over the roster Engage lists, never the board's role-scoped page.
  const unreadQuery = useUnreadConversationCount();
  const unreadCount = unreadQuery.data ?? 0;
  const unreadUnknown = unreadQuery.data === undefined && unreadQuery.isError;
  const refetchUnread = unreadQuery.refetch;
  const isIdle = useIdleAware(IDLE_THRESHOLD_MS);
  const onEngage = isActive(location.pathname, 'engage');
  useEffect(() => {
    // Engage refreshes the badge on its own tick, together with its list.
    if (onEngage) return undefined;
    const id = setInterval(() => {
      if (!isIdle()) refetchUnread();
    }, UNREAD_REFRESH_MS);
    return () => clearInterval(id);
  }, [onEngage, isIdle, refetchUnread]);
  let engageLabel = 'Engage';
  if (unreadUnknown) engageLabel = 'Engage, unread count unavailable';
  else if (unreadCount > 0) engageLabel = `Engage, ${unreadCount} unread conversation${unreadCount === 1 ? '' : 's'}`;

  // RootLayout sits above the route that owns `:programid`, so we read it off
  // `location.pathname` with a regex instead of `useParams()`.
  const homeTarget = React.useMemo(() => {
    const match = location.pathname.match(/^\/team\/([^/]+)/);
    const fromUrl = match?.[1] ?? null;
    const fromStorage = fromUrl ? null : getLastSelectedProgramId();
    const programid = fromUrl ?? fromStorage;
    return programid ? `/team/${programid}` : '/';
  }, [location.pathname]);

  const allowedTabs = (userRole && ROLE_TABS[userRole as keyof typeof ROLE_TABS]) || ROLE_TABS.clinician;
  const navItems = ALL_NAV_ITEMS
    .filter((item) => allowedTabs.includes(item.id))
    .map((item) => ({
      ...item,
      path: item.id === 'home' ? homeTarget : `/${item.id}`,
    }));

  return (
    <div className="layout-component">
      <PhoneCallBanner />

      <aside className="sidebar-component">
        <div className="nav-component">
          <div className="nav-component-wrapper">
            <div className="nav-logo">
              <img src="/favicon.png" alt="Withings" style={{ borderRadius: 6 }} />
            </div>
          </div>
        </div>

        <div className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              className={`sidebar-nav-item ${isActive(location.pathname, item.id) ? 'active' : ''}`}
              data-tooltip={item.label}
              aria-label={item.id === 'engage' ? engageLabel : item.label}
              style={{ position: 'relative' }}
            >
              {item.icon}
              {item.id === 'engage' && unreadUnknown && (
                <span aria-hidden="true" className="sidebar-nav-unread-unknown" title="Unread count unavailable" />
              )}
              {item.id === 'engage' && unreadCount > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    padding: '0 4px',
                    background: 'var(--wi-status-unread)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: '16px',
                    textAlign: 'center',
                    boxShadow: '0 0 0 2px var(--wi-background-secondary)',
                  }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="sidebar-role-wrap">
          <button
            ref={roleButtonRef}
            type="button"
            className="sidebar-role-chip"
            onClick={toggleRoleMenu}
            aria-label={`Current role: ${getRoleConfig(userRole)?.label ?? 'unknown'}. Click to switch.`}
            aria-haspopup="menu"
            aria-expanded={roleMenuOpen}
            data-tooltip={getRoleConfig(userRole)?.label ?? 'Switch role'}
            style={{ color: getRoleConfig(userRole)?.color }}
          >
            {(() => {
              const Icon = getRoleConfig(userRole)?.Icon;
              return Icon ? <Icon size={18} /> : null;
            })()}
          </button>
        </div>

        <div className="sidebar-profile-wrap">
          <button
            ref={profileButtonRef}
            type="button"
            className="sidebar-profile"
            onClick={toggleProfileMenu}
            aria-label="Open profile menu"
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
            }}
          >
            <Media
              src="/dr-tarzan.png"
              alt=""
              size={36}
              fallback={computeInitials(currentUser?.firstname, currentUser?.lastname, account?.email)}
            />
          </button>
        </div>
      </aside>

      {roleMenuOpen && roleAnchor && createPortal(
        <div
          ref={roleMenuRef}
          className="profile-menu"
          role="menu"
          style={{ left: roleAnchor.left, bottom: roleAnchor.bottom }}
        >
          <div className="profile-menu-header">
            <span className="eyebrow profile-menu-section-label">Current role</span>
          </div>
          {ROLE_CONFIG.map((role) => (
            <button
              key={role.id}
              type="button"
              className={`profile-menu-item profile-menu-role${role.id === userRole ? ' is-active' : ''}`}
              role="menuitemradio"
              aria-checked={role.id === userRole}
              onClick={() => {
                handleRoleChangeRouted(role.id);
                closeRoleMenu();
              }}
            >
              <span className="profile-menu-role-icon" style={{ color: role.color }}>
                <role.Icon size={14} />
              </span>
              <span>{role.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}

      {profileMenuOpen && menuAnchor && createPortal(
        <div
          ref={profileMenuRef}
          className="profile-menu"
          role="menu"
          style={{ left: menuAnchor.left, bottom: menuAnchor.bottom }}
        >
          <div className="profile-menu-header">
            <p className="body2 profile-menu-name">
              {[currentUser?.firstname, currentUser?.lastname].filter(Boolean).join(' ')
                || account?.email
                || 'Signed in'}
            </p>
            {account?.email && (
              <p className="detail1 text-secondary profile-menu-email">{account.email}</p>
            )}
          </div>
          <div className="profile-menu-divider" />
          <div className="profile-menu-section">
            <span className="eyebrow profile-menu-section-label">Theme</span>
            <ThemeToggle />
          </div>
          <div className="profile-menu-divider" />
          {/* "Switch care team" chip removed — Neo RPM serves a single program
              today, so `/` resolves the env program and redirects. The picker
              stays reachable at `/?switch=1` for the multi-program future. */}
          <button
            type="button"
            className="profile-menu-item"
            role="menuitem"
            onClick={openSettings}
          >
            Set session key
          </button>
          <div className="profile-menu-divider" />
          <button
            type="button"
            className="profile-menu-item profile-menu-item-danger"
            role="menuitem"
            onClick={logout}
          >
            Logout
          </button>
        </div>,
        document.body,
      )}

      <main className="main-content">
        {loading && (
          <div className="loading-center" style={{ minHeight: '60vh' }}>
            <div className="loading-spinner" />
            <span className="loading-center-text">Loading patients…</span>
          </div>
        )}

        {error && (
          <div style={{ padding: '48px 0' }}>
            <p className="body2" style={{ color: 'var(--wi-status-bad)' }}>
              Error:
              {error}
            </p>
            <button type="button" className="btn-primary" style={{ marginTop: 12 }} onClick={openSettings}>
              Update session key
            </button>
          </div>
        )}

        {!loading && !error && (
          <AppErrorBoundary>
            <Outlet />
          </AppErrorBoundary>
        )}
      </main>

      {showSettingsModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSettingsModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowSettingsModal(false); }}
          role="presentation"
        >
          {/* Stop click + key propagation so clicking inside the dialog doesn't
              dismiss it via the overlay handler. The dialog role IS interactive
              per ARIA, but jsx-a11y treats it as non-interactive — disable that
              specific check on the wrapper element. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            className="modal-content"
            style={{ maxWidth: 400 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-modal-title"
          >
            <div className="modal-header">
              <h2 id="settings-modal-title" className="header3" style={{ margin: 0 }}>Session key</h2>
              <button type="button" className="modal-close" onClick={() => setShowSettingsModal(false)} aria-label="Close">
                &times;
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                {/* autoFocus on the session-key input is intentional UX: the
                    only reason a user opens this modal is to paste a key, so
                    we save them a click. The a11y warning is acknowledged. */}
                <input
                  type="text"
                  className="call-notes-input"
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                  placeholder="Paste your corpsession_key from DevTools..."
                  value={sessionInput}
                  onChange={(e) => setSessionInput(e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <p className="detail1 text-secondary" style={{ margin: '6px 0 0' }}>
                  care.withings.com &rarr; DevTools &rarr; Application &rarr; Cookies &rarr; corpsession_key
                </p>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowSettingsModal(false)}>Cancel</button>
                <button type="button" className="btn-primary" onClick={saveSettings}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
